<script lang="ts">
import { computed, defineAsyncComponent, nextTick } from 'vue';
import { useStoreNavigationFooter } from '../../store/navigation/footer';
import { isMobile } from '../../utils/breakpoints';
import BadgeListDesktop1XP from '../BadgeList1XP/BadgeListDesktop1XP.vue';
import BadgeListMobile1XP from '../BadgeList1XP/BadgeListMobile1XP.vue';
import Text from './components/Text/Text.vue';
import Copyright from './components/Copyright/Copyright.vue';
import Navigation from './components/Navigation/Navigation.vue';
import Social from './components/Social/Social.vue';
import Contact from './components/Contact/Contact.vue';
import { mapBadges } from './helpers/badges';
import { mapCopyright } from './helpers/copyright';
import { useStoreNavigationSocial } from '../../store/navigation/socialSingle';
import { mapSocial } from './helpers/social';
import { useStoreConfiguration } from '../../store/configuration/configuration';
import { useStoreTranslations } from '../../store/translations/translations';
import { mapCols } from './helpers/cols';
import Divider from '../Divider/Divider.vue';
</script>

<script setup lang="ts">
const Payment = defineAsyncComponent(() => import('./components/Payment/Payment.vue'));

const storeNavigationFooter = useStoreNavigationFooter();
const storeNavigationSocial = useStoreNavigationSocial();
const storeTranslations = useStoreTranslations();

const storeConfiguration = useStoreConfiguration();
const instance = storeConfiguration.caasConfig?.instance;

const badgeListItems = computed(() => mapBadges(storeNavigationFooter.config));

const cols = computed(() =>
  mapCols({
    config: storeConfiguration.config,
    translations: storeTranslations.translations,
    instance,
  }),
);

const visibleSocialItems = computed(() => {
  return (
    storeNavigationSocial.items &&
    storeNavigationSocial.items[0].children?.filter((item) => !item.hideInNavigation)
  );
});

const copyRightPrimaryText = computed(() => mapCopyright(storeNavigationFooter.config));
const social = computed(() => mapSocial(visibleSocialItems.value));
nextTick(() => {
  document.querySelectorAll('[id="textTeaserText_"], .TextTeaser__text').forEach((el) => {
    const inner = el.querySelector('[id="textTeaserTextInner_"]');
    if (inner && !inner.textContent?.trim()) {
      (el as HTMLElement).style.display = 'none';
    }
  });
});
</script>

<template>
  <footer
    v-if="storeNavigationFooter.config && instance"
    :class="[$style.container, instance === 'shop' ? $style.shop : $style.website]"
  >
    <component
      :is="isMobile ? BadgeListMobile1XP : BadgeListDesktop1XP"
      v-if="badgeListItems?.length && badgeListItems.length > 0"
      aria-label="Benefits badges"
      :class="$style.badgeList"
      :items="badgeListItems"
    />

    <Divider />
    <div :class="$style.layout">
      <div v-for="(col, index) in cols" :key="col?.id" :class="$style.col">
        <Navigation
          v-if="col.type === 'navigation'"
          :index="index"
          :instance="instance"
          :items="col.items"
          :title="col.title"
        />
        <Payment v-if="col.type === 'payment'" :items="col.items" :title="col.title" />
        <Text
          v-if="col.type === 'text' || (col.type === 'contact' && !col.contactDefault)"
          :additional-class="col.type"
          :items="col.items"
          :title="col.title"
        />
        <Contact
          v-if="
            col.type === 'contact' &&
            col.contactDefault &&
            storeNavigationFooter.config.pt_default_contact
          "
          :additional-class="col.type"
          :button="col.showButton ? storeNavigationFooter.config.pt_contact_page : undefined"
          :items="storeNavigationFooter.config.pt_default_contact"
          :title="col.title"
        />
      </div>
    </div>

    <div :class="[$style.layout, $style.lower]">
      <Copyright v-if="copyRightPrimaryText" :primary-text="copyRightPrimaryText" />
      <Social v-if="social?.items" :items="social.items" />
    </div>
  </footer>
</template>

<style module lang="scss" src="./Footer1XP.scss" />
